import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { extractErrorMessage } from '../utils/errorHandler';
import { formatPrice } from '../utils/formatters';
import ProductFilters from './ProductFilters'; // Add this import
import './OfferGenerator.css';

// DND-KIT IMPORTS
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import {
  SlideType,
  InterludeContent,
  SlideItem,
  Product,
  SelectedProduct,
  Template,
  OfferData,
  ProductType,
  OfferSlideItem,
  OfferSavePayload,
  SavedOfferDetail
} from '../types';

// Draggable row for dnd-kit
function DraggableRow({ id, children }: { id: string, children: React.ReactNode }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    background: isDragging ? '#f0f0f0' : undefined,
    cursor: 'grab',
    opacity: isDragging ? 0.8 : 1,
    zIndex: isDragging ? 100 : 'auto',
  };
  return (
    <tr ref={setNodeRef} style={style} {...attributes} {...listeners}>
      {children}
    </tr>
  );
}

const OfferGenerator: React.FC = () => {
  const [products, setProducts] = useState<Product[]>([]);
  const [filteredProducts, setFilteredProducts] = useState<Product[]>([]);
  const [selectedProducts, setSelectedProducts] = useState<SelectedProduct[]>([]);
  const [slides, setSlides] = useState<SlideItem[]>([]);
  
  // Replace individual filter states with a unified filters object
  const [filters, setFilters] = useState({
    name: '',
    brand: '',
    country: '',
    region: '',
    product_type_id: '',
    productType: 'all',
    category: '',
    category_id: '',
    subcategory_id: '',
    vintage: '',
    alcoholMin: '',
    alcoholMax: '',
    priceMin: '',
    priceMax: '',
    userId: 'all',
    grape_variety: '',
    characteristics: '',
    features: ''
  });
  
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [productTypes, setProductTypes] = useState<ProductType[]>([]);
  
  const [offerData, setOfferData] = useState<OfferData>({
    title: 'Oferta Handlowa',
    products: []
  });
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState('');

  // Saved offer we are editing (null = not saved yet)
  const [currentOfferId, setCurrentOfferId] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveInfo, setSaveInfo] = useState('');
  const [showAllProducts, setShowAllProducts] = useState<boolean>(false);
  const [templates, setTemplates] = useState<Template[]>([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState<number | null>(null);
  
  // Add price type state
  const [selectedPriceType, setSelectedPriceType] = useState<'price_net' | 'price_alt'>('price_net');
  
  // Add sorting state
  const [sortField, setSortField] = useState<'name' | 'price_net' | 'price_alt' | 'vintage' | 'alcohol_content' | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  
  // Add local state for price inputs (separate from filters)
  const [priceInputs, setPriceInputs] = useState({
    priceMin: '',
    priceMax: ''
  });
  
  // Add ref for debounce timers
  const priceDebounceTimer = useRef<NodeJS.Timeout | null>(null);

  const navigate = useNavigate();
  const { id: offerIdParam } = useParams<{ id?: string }>();

  // Get unique options for dropdowns
  const countryOptions = Array.from(new Set(products.map((p) => p.country).filter(Boolean))).sort();
  const brandOptions = Array.from(new Set(products.map((p) => p.brand).filter(Boolean))).sort();
  const vintageOptions = Array.from(new Set(products.map((p) => p.vintage).filter((v): v is number => Boolean(v)))).sort((a, b) => b - a);

  // Fetch product types on component mount
  useEffect(() => {
    const fetchProductTypes = async () => {
      try {
        const response = await axios.get('/api/product-types');
        setProductTypes(response.data);
      } catch (err: any) {
        console.error('Error fetching product types:', err);
      }
    };
    
    fetchProductTypes();
  }, []);

  const fetchProducts = useCallback(async () => {
    try {
      setLoading(true);
      
      const params = new URLSearchParams();
      params.append('include_all', showAllProducts ? 'true' : 'false');
      
      // Apply filters from the unified filter state
      if (filters.product_type_id) params.append('product_type_id', filters.product_type_id);
      if (filters.name) params.append('name', filters.name);
      if (filters.brand) params.append('brand', filters.brand);
      if (filters.country) params.append('country', filters.country);
      if (filters.region) params.append('region', filters.region);
      if (filters.category_id) params.append('category_id', filters.category_id);
      if (filters.subcategory_id) params.append('subcategory_id', filters.subcategory_id);
      if (filters.vintage) params.append('vintage', filters.vintage);
      if (filters.grape_variety) params.append('grape_variety', filters.grape_variety);
      if (filters.alcoholMin) params.append('alcohol_content_min', filters.alcoholMin);
      if (filters.alcoholMax) params.append('alcohol_content_max', filters.alcoholMax);
      if (filters.priceMin) params.append('price_min', filters.priceMin);  // Add this
      if (filters.priceMax) params.append('price_max', filters.priceMax);  // Add this
      if (filters.features) params.append('features', filters.features);
      
      const response = await axios.get(`/api/products?${params.toString()}`);
      setProducts(response.data);
      setFilteredProducts(response.data);
      
    } catch (err: any) {
      setError(extractErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [filters, showAllProducts]);

  // Handle filter changes
  const handleFilterChange = useCallback((field: string, value: string) => {
    console.log(`Filter changed: ${field} = ${value}`);
    setFilters(prev => ({ ...prev, [field]: value }));
  }, []);
  
  // Handle product type change
  const handleProductTypeChange = useCallback((typeId: string) => {
    console.log(`Product type changed to: ${typeId}`);
    setFilters(prev => ({
      ...prev,
      product_type_id: typeId,
      productType: typeId || 'all',
      // Reset dependent fields
      category_id: '',
      subcategory_id: ''
    }));
  }, []);

  // Fetch products when filters change
  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  // Fetch templates
  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        const response = await axios.get('/api/templates');
        setTemplates(response.data);
        // Only preselect the default for a brand new offer - a saved offer brings its own template
        if (!offerIdParam) {
          const defaultTemplate = response.data.find((t: Template) => t.is_default);
          if (defaultTemplate) {
            setSelectedTemplateId(defaultTemplate.id);
          }
        }
      } catch (err: any) {
        console.error('Error fetching templates:', err);
      }
    };
    fetchTemplates();
  }, [offerIdParam]);
  
  // Centralize state updates through the 'slides' array
  useEffect(() => {
    const newSelectedProducts = slides
      .filter((slide): slide is SlideItem & { type: 'product' } => slide.type === 'product')
      .map(slide => slide.content as SelectedProduct);
    
    // Check for differences to prevent infinite loops
    if (JSON.stringify(newSelectedProducts) !== JSON.stringify(selectedProducts)) {
      setSelectedProducts(newSelectedProducts);
    }
  }, [slides]);

  const handleProductSelect = (product: Product) => {
    setSlides(prevSlides => {
      const exists = prevSlides.some(s => s.type === 'product' && (s.content as Product).id === product.id);
      if (exists) {
        return prevSlides.filter(s => !(s.type === 'product' && (s.content as Product).id === product.id));
      } else {
        // Use the selected price type when adding product
        const initialPrice = selectedPriceType === 'price_net' ? product.price_net : (product.price_alt || product.price_net);
        
        const newProductSlide: SlideItem = {
          id: `product-${product.id}`,
          type: 'product',
          content: { 
            ...product, 
            quantity: 1, 
            discount: 0,
            price_net: initialPrice,
          },
        };
        return [...prevSlides, newProductSlide];
      }
    });
  };

  const updateSlideContent = (slideId: string, newContent: Partial<SelectedProduct | InterludeContent>) => {
    setSlides(prevSlides =>
      prevSlides.map(slide =>
        slide.id === slideId
          ? { ...slide, content: { ...slide.content, ...newContent } }
          : slide
      )
    );
  };
  
  const handleQuantityChange = (productId: number, quantity: number) => {
    updateSlideContent(`product-${productId}`, { quantity });
  };
  
  const handlePriceChange = (productId: number, price_net: number) => {
    updateSlideContent(`product-${productId}`, { price_net });
  };
  
  const handleDiscountChange = (productId: number, discount: number) => {
    updateSlideContent(`product-${productId}`, { discount });
  };
  
  const handleRemoveProduct = (productId: number) => {
    setSlides(slides.filter(s => s.id !== `product-${productId}`));
  };

  // Reset filters
  const resetFilters = () => {
    setFilters({
      name: '',
      brand: '',
      country: '',
      region: '',
      product_type_id: '',
      productType: 'all',
      category: '',
      category_id: '',
      subcategory_id: '',
      vintage: '',
      alcoholMin: '',
      alcoholMax: '',
      priceMin: '',
      priceMax: '',
      userId: 'all',
      grape_variety: '',
      characteristics: '',
      features: ''
    });
    setShowAllProducts(false);
  };

  // Add an interlude slide
  const addInterludeSlide = () => {
    const newInterlude: SlideItem = {
      id: `interlude-${Date.now()}`,
      type: 'interlude',
      content: { text: 'Nowy Przerywnik' },
    };
    setSlides(prev => [...prev, newInterlude]);
  };

  // Remove an interlude slide by its unique ID
  const removeInterludeSlide = (slideId: string) => {
    setSlides(slides.filter(s => s.id !== slideId));
  };
  
  // Update interlude text by its unique ID
  const updateInterludeText = (slideId: string, text: string) => {
    updateSlideContent(slideId, { text });
  };

  // ===== Saving / restoring a saved offer =====

  // Flat, ordered snapshot of the builder. The array order IS the layout.
  const buildSlidesPayload = (): OfferSlideItem[] =>
    slides.map(slide => {
      if (slide.type === 'product') {
        const product = slide.content as SelectedProduct;
        return {
          type: 'product' as SlideType,
          product_id: product.id,
          price_net: product.price_net ?? null,
          quantity: product.quantity ?? 1,
          discount: product.discount ?? 0,
        };
      }
      return {
        type: 'interlude' as SlideType,
        text: (slide.content as InterludeContent).text,
      };
    });

  const saveOffer = async (asNew: boolean = false) => {
    setSaving(true);
    setError('');
    setSaveInfo('');

    const payload: OfferSavePayload = {
      title: offerData.title,
      client_name: offerData.client_name,
      client_email: offerData.client_email,
      description: offerData.description,
      template_id: selectedTemplateId,
      price_type: selectedPriceType,
      slides: buildSlidesPayload(),
    };

    try {
      const response = currentOfferId && !asNew
        ? await axios.put<SavedOfferDetail>(`/api/offers/${currentOfferId}`, payload)
        : await axios.post<SavedOfferDetail>('/api/offers', payload);

      if (response.data.id !== currentOfferId) {
        setCurrentOfferId(response.data.id);
        navigate(`/offer-generator/${response.data.id}`, { replace: true });
      }
      setSaveInfo(`Oferta "${response.data.title}" została zapisana.`);
    } catch (err: any) {
      setError(`Błąd podczas zapisywania oferty: ${extractErrorMessage(err)}`);
    } finally {
      setSaving(false);
    }
  };

  // Restore a saved offer: edited prices, product order and interlude positions
  useEffect(() => {
    if (!offerIdParam) return;

    const offerId = parseInt(offerIdParam, 10);
    if (Number.isNaN(offerId) || offerId === currentOfferId) return;

    const loadOffer = async () => {
      try {
        const response = await axios.get<SavedOfferDetail>(`/api/offers/${offerId}`);
        const offer = response.data;
        const productsById = new Map(offer.products.map(p => [p.id, p]));

        const restoredSlides: SlideItem[] = [];
        offer.slides.forEach((item, index) => {
          if (item.type === 'product') {
            const product = item.product_id != null ? productsById.get(item.product_id) : undefined;
            if (!product) return; // reported via missing_product_ids below
            restoredSlides.push({
              id: `product-${product.id}`,
              type: 'product',
              content: {
                ...product,
                quantity: item.quantity ?? 1,
                discount: item.discount ?? 0,
                // The price saved with the offer wins over the current catalog price
                price_net: item.price_net ?? product.price_net,
              },
            });
          } else {
            restoredSlides.push({
              id: `interlude-${index}-${Date.now()}`,
              type: 'interlude',
              content: { text: item.text || '' },
            });
          }
        });

        setSlides(restoredSlides);
        setOfferData({
          id: offer.id,
          title: offer.title,
          client_name: offer.client_name || '',
          client_email: offer.client_email || '',
          description: offer.description || '',
          products: [],
        });
        setSelectedTemplateId(offer.template_id ?? null);
        setSelectedPriceType(offer.price_type === 'price_alt' ? 'price_alt' : 'price_net');
        setCurrentOfferId(offer.id);

        if (offer.missing_product_ids.length > 0) {
          setError(`Pominięto produkty usunięte z katalogu (ID: ${offer.missing_product_ids.join(', ')}).`);
        }
      } catch (err: any) {
        setError(`Nie udało się wczytać zapisanej oferty: ${extractErrorMessage(err)}`);
      }
    };

    loadOffer();
  }, [offerIdParam, currentOfferId]);

  const generateOffer = async () => {
    setGenerating(true);
    setError('');
    
    try {
      const orderedProducts = slides.filter(s => s.type === 'product').map(s => s.content as SelectedProduct);
      
      const interludeData = slides.map((slide, index) => {
        if (slide.type === 'interlude') {
          const productsBefore = slides.slice(0, index).filter(s => s.type === 'product').length;
          return {
            text: (slide.content as InterludeContent).text,
            position: productsBefore,
          };
        }
        return null;
      }).filter(Boolean);

      // NEW: Create a single, structured payload for products
      const productsPayload = orderedProducts.map(p => ({
        id: p.id,
        quantity: p.quantity,
        discount: p.discount || 0,
        price_net: p.price_net, // Send the final price from the input
      }));

      const requestData = {
        title: offerData.title,
        client_name: offerData.client_name,
        client_email: offerData.client_email,
        description: offerData.description,
        template_id: selectedTemplateId,
        
        // REMOVE THE OLD, SEPARATE LISTS
        // product_ids: orderedProducts.map(p => p.id),
        // quantities: orderedProducts.map(p => p.quantity),
        // discounts: orderedProducts.map(p => p.discount || 0),
        // prices: orderedProducts.map(p => p.price_net),

        // ADD THE NEW, UNIFIED LIST
        products: productsPayload,
        
        interlude_slides: interludeData,
      };

      const response = await axios.post('/api/slides/generate', requestData, { responseType: 'blob' });

      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${offerData.title}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
    } catch (err: any) {
      if (err.response?.data instanceof Blob) {
        const errorText = await err.response.data.text();
        setError(`Błąd podczas generowania oferty PDF: ${errorText}`);
      } else {
        setError(`Błąd podczas generowania oferty PDF: ${err.response?.data?.detail || err.message}`);
      }
    } finally {
      setGenerating(false);
    }
  };
  
  // Add sorting function
  const handleSort = (field: 'name' | 'price_net' | 'price_alt' | 'vintage' | 'alcohol_content') => {
    const newDirection = sortField === field && sortDirection === 'asc' ? 'desc' : 'asc';
    setSortField(field);
    setSortDirection(newDirection);
    
    const sorted = [...filteredProducts].sort((a, b) => {
      let aValue = a[field];
      let bValue = b[field];
      
      // Handle null/undefined values
      if (aValue === null || aValue === undefined) return 1;
      if (bValue === null || bValue === undefined) return -1;
      
      // String comparison for name
      if (field === 'name') {
        aValue = aValue.toString().toLowerCase();
        bValue = bValue.toString().toLowerCase();
      }
      
      if (aValue < bValue) return newDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return newDirection === 'asc' ? 1 : -1;
      return 0;
    });
    
    setFilteredProducts(sorted);
  };
  
  // Render sort icon
  const renderSortIcon = (field: 'name' | 'price_net' | 'price_alt' | 'vintage' | 'alcohol_content') => {
    if (sortField !== field) {
      return <span className="is-dragging">⇅</span>;
    }
    return sortDirection === 'asc' ? '↑' : '↓';
  };
  
  const sortSelectedProducts = (criteria: 'price_asc' | 'price_desc') => {
    setSlides(prevSlides => {
        const productSlides = prevSlides.filter(s => s.type === 'product');

        productSlides.sort((a, b) => {
            const priceA = (a.content as SelectedProduct).price_net || 0;
            const priceB = (b.content as SelectedProduct).price_net || 0;
            return criteria === 'price_asc' ? priceA - priceB : priceB - priceA;
        });

        // Sort products only; interludes stay at the exact positions they were placed at,
        // so the layout the user built (and saved) survives a re-sort.
        let nextProduct = 0;
        return prevSlides.map(slide =>
          slide.type === 'product' ? productSlides[nextProduct++] : slide
        );
    });
  };

  // DND-KIT sensors and handler
  const sensors = useSensors(useSensor(PointerSensor));
  
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      setSlides((items) => {
        const oldIndex = items.findIndex((item) => item.id === active.id);
        const newIndex = items.findIndex((item) => item.id === over.id);
        return arrayMove(items, oldIndex, newIndex);
      });
    }
  };

  // Add debounced price filter handler
  const handlePriceInputChange = (field: 'priceMin' | 'priceMax', value: string) => {
    // Update local input state immediately for responsive UI
    setPriceInputs(prev => ({ ...prev, [field]: value }));
    
    // Clear existing timer
    if (priceDebounceTimer.current) {
      clearTimeout(priceDebounceTimer.current);
    }
    
    // Set new timer to update filters after 2 seconds
    priceDebounceTimer.current = setTimeout(() => {
      setFilters(prev => ({ ...prev, [field]: value }));
    }, 2000);
  };
  
  // Handle blur event - apply filter immediately
  const handlePriceBlur = (field: 'priceMin' | 'priceMax') => {
    if (priceDebounceTimer.current) {
      clearTimeout(priceDebounceTimer.current);
    }
    setFilters(prev => ({ ...prev, [field]: priceInputs[field] }));
  };
  
  // Handle Enter key - apply filter immediately
  const handlePriceKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, field: 'priceMin' | 'priceMax') => {
    if (e.key === 'Enter') {
      if (priceDebounceTimer.current) {
        clearTimeout(priceDebounceTimer.current);
      }
      setFilters(prev => ({ ...prev, [field]: priceInputs[field] }));
    }
  };
  
  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (priceDebounceTimer.current) {
        clearTimeout(priceDebounceTimer.current);
      }
    };
  }, []);
  
  // Initialize local price inputs from filters
  useEffect(() => {
    setPriceInputs({
      priceMin: filters.priceMin,
      priceMax: filters.priceMax
    });
  }, [filters.priceMin, filters.priceMax]);

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"><span className="sr-only">Ładowanie produktów...</span></div>
      </div>
    );
  }

  return (
    <div className="offer-generator-container">
      {/* Header and Error unchanged */}
      <div className="offer-generator-header">
        <div className="header-content">
          <div>
            <h1>Generator Ofert</h1>
            <p>Wybierz produkty i wygeneruj ofertę handlową</p>
          </div>
        </div>
      </div>

      {error && (
        <div className="error-alert">
          {error}
          <button className="error-close" onClick={() => setError('')}>×</button>
        </div>
      )}

      {saveInfo && (
        <div className="save-info-alert">
          {saveInfo}
          <Link to="/offers" className="save-info-link">Moje oferty</Link>
          <button className="error-close" onClick={() => setSaveInfo('')}>×</button>
        </div>
      )}
      
      <div className="main-offer-card">
        {/* Offer Configuration Section - unchanged */}
        <div className="card-header">
          <h5>Konfiguracja oferty</h5>
        </div>
        <div className="card-body">
           <div className="config-grid">
            <div className="config-field">
              <label htmlFor="title">Nazwa pliku</label>
              <input
                type="text"
                id="title"
                value={offerData.title}
                onChange={(e) => setOfferData({ ...offerData, title: e.target.value })}
                className="form-input"
              />
            </div>
            <div className="config-field">
              <label htmlFor="client_name">Nazwa klienta</label>
              <input
                type="text"
                id="client_name"
                value={offerData.client_name || ''}
                onChange={(e) => setOfferData({ ...offerData, client_name: e.target.value })}
                className="form-input"
              />
            </div>
             <div className="config-field">
              <label htmlFor="client_email">Email klienta</label>
              <input
                type="email"
                id="client_email"
                value={offerData.client_email || ''}
                onChange={(e) => setOfferData({ ...offerData, client_email: e.target.value })}
                className="form-input"
              />
            </div>
          </div>
           <div className="config-grid-one">
            <div className="config-field">
              <label htmlFor="template-select">Wybierz szablon oferty</label>
              <select
                id="template-select"
                className="form-select"
                value={selectedTemplateId || ''}
                onChange={(e) => setSelectedTemplateId(e.target.value ? parseInt(e.target.value) : null)}
              >
                <option value="">-- Wybierz szablon --</option>
                {templates.map((template) => (
                  <option key={template.id} value={template.id}>
                    {template.name} {template.is_default ? '(Domyślny)' : ''}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Add price selection radio buttons */}
          <div className="config-field">
            <label>Domyślna cena dla produktów:</label>
            <div className="price-selection">
              <label className="price-option">
                <input
                  type="radio"
                  name="priceType"
                  value="price_net"
                  checked={selectedPriceType === 'price_net'}
                  onChange={() => setSelectedPriceType('price_net')}
                />
                <span>Cena netto</span>
              </label>
              <label className="price-option">
                <input
                  type="radio"
                  name="priceType"
                  value="price_alt"
                  checked={selectedPriceType === 'price_alt'}
                  onChange={() => setSelectedPriceType('price_alt')}
                />
                <span>Cena alternatywna</span>
              </label>
            </div>
          </div>
        </div>
        {/* Selected Products Section */}
        {slides.length > 0 && (
          <>
            <hr className="section-divider" />
            <div className="selected-products-header">
              <h5 className="mb-0">
                Wybrane slajdy ({slides.length})
                {currentOfferId && <span className="saved-offer-badge">zapisana oferta #{currentOfferId}</span>}
              </h5>
              <div className="sorting-controls">
                <span>Sortowanie:</span>
                <button onClick={() => sortSelectedProducts('price_asc')} className="btn-sm btn-outline-secondary">Cena ↑</button>
                <button onClick={() => sortSelectedProducts('price_desc')} className="btn-sm btn-outline-secondary">Cena ↓</button>
                <button onClick={addInterludeSlide} className="btn-sm btn-outline-info">+ Przerywnik</button>
              </div>
              <div className="offer-actions">
                <button
                  onClick={() => saveOffer(false)}
                  disabled={saving || selectedProducts.length === 0 || !offerData.title}
                  className="save-offer-btn"
                >
                  {saving ? 'Zapisywanie...' : 'Zapisz ofertę'}
                </button>
                {currentOfferId && (
                  <button
                    onClick={() => saveOffer(true)}
                    disabled={saving || selectedProducts.length === 0 || !offerData.title}
                    className="save-as-new-btn"
                  >
                    Zapisz jako nową
                  </button>
                )}
                <button
                  onClick={generateOffer}
                  disabled={generating || selectedProducts.length === 0 || !selectedTemplateId}
                  className="generate-offer-btn"
                >
                  {generating ? 'Generowanie PDF...' : `Generuj PDF (${selectedProducts.length})`}
                </button>
              </div>
            </div>
            <div className="table-container">
              <div className="table-responsive">
                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                  <SortableContext items={slides.map(s => s.id)} strategy={verticalListSortingStrategy}>
                    <table className="selected-products-table">
                      <thead className="table-light">
                        <tr>
                          <th>Zdjęcie</th>
                          <th>Nazwa</th>
                          <th>Marka</th>
                          <th>Cena netto</th>
                          <th>Akcje</th>
                        </tr>
                      </thead>
                      <tbody>
                        {slides.map((slide) => {
                          if (slide.type === 'product') {
                            const product = slide.content as SelectedProduct;
                            return (
                              <DraggableRow key={slide.id} id={slide.id}>
                                <td>
                                  <img src={product.image_url || '/placeholder.png'} alt={product.name} className="offer-thumb"/>
                                </td>
                                <td>{product.name}</td>
                                <td>{product.brand || 'N/A'}</td>
                                <td>
                                  <span className="price-input">
                                    <input
                                      type="number"
                                      min="0"
                                      step="0.01"
                                      aria-label={`Cena netto: ${product.name}`}
                                      value={product.price_net ?? ''}
                                      onChange={(e) => handlePriceChange(product.id, parseFloat(e.target.value))}
                                      onPointerDown={e => e.stopPropagation()}
                                    />
                                    <span className="price-input__unit">zł</span>
                                  </span>
                                </td>
                                <td>
                                  <button className="btn-outline-danger btn-sm" onClick={() => handleRemoveProduct(product.id)} onPointerDown={e => e.stopPropagation()}>✕</button>
                                </td>
                              </DraggableRow>
                            );
                          } else { // Interlude slide
                            const interlude = slide.content as InterludeContent;
                            return (
                              <DraggableRow key={slide.id} id={slide.id}>
                                <td colSpan={6} className="interlude-preview-row">
                                  <div className="interlude-preview">
                                    <div className="interlude-icon is-draggable" aria-hidden="true">⠿</div>
                                    <div className="interlude-text">
                                      <span className="badge interlude-badge">Przerywnik</span>
                                      <input
                                        type="text"
                                        className="interlude-input"
                                        value={interlude.text}
                                        placeholder="Tytuł przerywnika"
                                        aria-label="Tytuł slajdu przerywnika"
                                        onChange={(e) => updateInterludeText(slide.id, e.target.value)}
                                        onPointerDown={(e) => e.stopPropagation()} // Prevent drag when clicking input
                                      />
                                    </div>
                                    <div className="interlude-actions">
                                      <button
                                        onClick={() => removeInterludeSlide(slide.id)}
                                        onPointerDown={(e) => e.stopPropagation()} // Prevent drag when clicking button
                                        className="btn-outline-danger btn-sm"
                                      >
                                        ✕
                                      </button>
                                    </div>
                                  </div>
                                </td>
                              </DraggableRow>
                            );
                          }
                        })}
                      </tbody>
                    </table>
                  </SortableContext>
                </DndContext>
              </div>
            </div>
          </>
        )}
        
        <hr className="section-divider" />
        <div className="card-header">
          <h5 className="card-title mb-0">Wybór produktów</h5>
        </div>
        
        {/* CHANGED: Modified layout to put filters on top */}
        <div className="products-layout-column">
          {/* MOVED: Filters section moved to top */}
          <div className="filters-section-top">
            <ProductFilters
              filters={{
                ...filters,
                // Pass the local input values to ProductFilters
                priceMin: priceInputs.priceMin,
                priceMax: priceInputs.priceMax
              }}
              onChange={handleFilterChange}
              onProductTypeChange={handleProductTypeChange}
              onPriceChange={handlePriceInputChange}
              onPriceBlur={handlePriceBlur}
              onPriceKeyDown={handlePriceKeyDown}
              brandOptions={brandOptions}
              countryOptions={countryOptions}
              vintageOptions={vintageOptions}
              productTypes={productTypes}
              resetFilters={resetFilters}
              className="offer-filters"
            />
            
            {/* Keep the show all products checkbox */}
            <div className="show-all-checkbox">
              <div className="form-check">
                <input
                  type="checkbox"
                  id="show_all_products"
                  checked={showAllProducts}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setShowAllProducts(e.target.checked)}
                  className="form-check-input"
                />
                <label htmlFor="show_all_products" className="form-check-label">
                  Pokaż wszystkie produkty (w tym nieaktywne)
                </label>
              </div>
            </div>
          </div>

          {/* Products section remains mostly the same */}
          <div className="products-section full-width">
            <div className="products-content">
              <div className="products-header">
                <h6 className="mb-0">Dostępne produkty</h6>
                <span className="badge bg-secondary">
                  {filteredProducts.length} z {products.length} produktów
                </span>
              </div>
              
              {filteredProducts.length === 0 ? (
                <div className="text-center py-5">
                  <h5>Brak produktów spełniających kryteria filtrowania</h5>
                  <button className="btn-link" onClick={resetFilters}>
                    Wyczyść filtry aby zobaczyć wszystkie produkty
                  </button>
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="products-table">
                    <thead className="table-dark">
                      <tr>
                        <th className="col-thumb">Zdjęcie</th>
                        <th
                          className="is-sortable"
                          aria-sort="none"
                          onClick={() => handleSort('name')}
                        >
                          Nazwa {renderSortIcon('name')}
                        </th>
                        <th>Marka</th>
                        <th>Kraj</th>
                        <th
                          className="is-sortable col-narrow"
                          aria-sort="none"
                          onClick={() => handleSort('vintage')}
                        >
                          Rocznik {renderSortIcon('vintage')}
                        </th>
                        <th
                          className="is-sortable col-narrow"
                          aria-sort="none"
                          onClick={() => handleSort('alcohol_content')}
                        >
                          Alk. % {renderSortIcon('alcohol_content')}
                        </th>
                        <th
                          className="is-sortable col-wide"
                          aria-sort="none"
                          onClick={() => handleSort('price_net')}
                        >
                          Cena netto {renderSortIcon('price_net')}
                        </th>
                        <th
                          className="is-sortable col-wide"
                          aria-sort="none"
                          onClick={() => handleSort('price_alt')}
                        >
                          Cena alt. {renderSortIcon('price_alt')}
                        </th>
                        <th className="col-wide">Akcje</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredProducts.map((product: Product) => {
                        const isSelected = selectedProducts.find((p: SelectedProduct) => p.id === product.id);
                        return (
                          <tr key={product.id} className={isSelected ? 'table-success' : ''}>
                            <td>
                              <img 
                                src={product.image_url || '/placeholder.png'} 
                                alt={product.name}
                                className="offer-thumb"
                              />
                            </td>
                            <td>
                              <div className="fw-bold">{product.name}</div>
                              {product.description_short && (
                                <small className="text-muted">{product.description_short}</small>
                              )}
                            </td>
                            <td>{product.brand || <span className="text-muted">Brak danych</span>}</td>
                            <td>{product.country || <span className="text-muted">Brak danych</span>}</td>
                            <td>{product.vintage || <span className="text-muted">N/A</span>}</td>
                            <td>{product.alcohol_content ? `${product.alcohol_content}%` : <span className="text-muted">N/A</span>}</td>
                            <td>
                              <strong>{formatPrice(product.price_net)} </strong>
                            </td>
                            <td>
                              <strong>{formatPrice(product.price_alt || product.price_net)} </strong>
                            </td>
                            <td>
                              <button
                                onClick={() => handleProductSelect(product)}
                                className={`btn-sm ${isSelected ? 'btn-warning' : 'btn-primary'}`}
                                title={isSelected ? 'Usuń z oferty' : 'Dodaj do oferty'}
                              >
                                {isSelected ? 'W ofercie' : 'Dodaj'}
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OfferGenerator;